import React from 'react';
import NavbarMenuItem from '@components/atoms/NavbarMenuItem';
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react';
import { RectangleGroupIcon, UserCircleIcon } from '@heroicons/react/24/solid';
import { Cog6ToothIcon, TableCellsIcon, SquaresPlusIcon } from '@heroicons/react/20/solid';
import { ArrowRightStartOnRectangleIcon } from '@heroicons/react/16/solid';
import { ChevronDownIcon } from '@heroicons/react/20/solid';
import { useGetCurrentUserQuery, useIsSignedIn } from '../../apiHooks/userApi';
import { useLogoutMutation } from '../../apiHooks/authApi';
import NavbarLink from '@components/atoms/NavbarLink';
import config from '../../config';
import ProfilePicture from '@components/atoms/ProfilePicture';
import { useLocation } from '@reach/router';

export default function NavbarMenu() {
    const isSignedIn = useIsSignedIn();
    const { data: user, isLoading } = useGetCurrentUserQuery();
    const [logout] = useLogoutMutation();
    const location = useLocation();

    if (!isSignedIn && !(location.pathname === '/client-routes/authenticated/dashboard/' && typeof window === 'undefined')) {
        return (
            <div className='hidden flex-none md:ml-4 md:flex md:items-center md:pr-0.5'>
                <NavbarLink
                    link='/sign-in/'
                    className='relative flex rounded-md px-3 py-1.5 bg-white/20 text-white text-sm font-medium hover:bg-white/30 hover:no-underline'
                >
                    Login
                </NavbarLink>
            </div>
        );
    }

    return (
        <div className='hidden flex-none md:flex md:items-center'>
            <Menu as='div' className='relative ml-4 shrink-0'>
                <MenuButton
                    className='relative -mr-2 flex items-center rounded-l-3xl rounded-r-md hover:bg-white/10 outline-none cursor-pointer'
                    title='User Menu'
                >
                    <span className='sr-only'>Open user menu</span>
                    <div className='flex items-center'>
                        {isLoading ? (
                            <div className='size-8 rounded-full border border-white/40 bg-slate-800/40 animate-pulse' />
                        ) : (
                            <ProfilePicture
                                accountHandle={user.account.handle}
                                className='size-8 rounded-full border border-white/40 bg-slate-800/40'
                                src={user.account.profile_picture_200x200 || config.placeholderProfileImage}
                            />
                        )}
                        {isLoading ? (
                            <div className='h-4 w-12 bg-white/30 rounded animate-pulse ml-2' />
                        ) : (
                            <span className='text-white text-sm font-medium pl-2'>
                                {user.account.display_name.split(/\s+/)[0]}
                            </span>
                        )}
                        <ChevronDownIcon className='size-5 text-white mr-1' />
                    </div>
                </MenuButton>
                <MenuItems
                    modal={false}
                    transition={true}
                    className='absolute right-0 z-10 mt-3.5 w-48 origin-top-right rounded-lg bg-white shadow-lg ring-1 ring-black/5 focus:outline-none data-[closed]:data-[leave]:scale-95 data-[closed]:data-[leave]:transform data-[closed]:data-[leave]:opacity-0 data-[leave]:transition data-[leave]:duration-75 data-[leave]:ease-in'
                >
                    <div className='flex items-center gap-3 bg-gray-100 rounded-t-lg px-4 py-2 border-b border-gray-300 mb-1'>
                        <p className='truncate leading-none' title={user?.account.display_name}>
                            <span className='text-base font-bold text-gray-900'>{user?.account.display_name}</span>
                            <br />
                            <span className='text-sm text-gray-600' title={user?.account.handle}>
                                {user?.account.handle}
                            </span>
                        </p>
                    </div>

                    <NavbarMenuItem name='Dashboard' link='/' icon={RectangleGroupIcon} />
                    <NavbarMenuItem name='Results' link='/results/' icon={TableCellsIcon} />
                    <NavbarMenuItem
                        name='Profile'
                        link={user?.account.handle ? `/${user.account.handle}/` : '/'}
                        icon={UserCircleIcon}
                    />
                    <NavbarMenuItem name='Settings' link='/settings/' icon={Cog6ToothIcon} />
                    <NavbarMenuItem name='New Application' link='/new/' icon={SquaresPlusIcon} />

                    <hr className='my-1' />

                    <MenuItem>
                        <button
                            onClick={() => logout()}
                            className='inline-flex items-center w-full px-4 py-2 mb-1.5 text-sm text-gray-600 hover:text-gray-800 data-[focus]:bg-gray-100 data-[focus]:outline-none hover:no-underline hover:bg-gray-100'
                        >
                            <ArrowRightStartOnRectangleIcon className='size-4 mr-2' /> Logout
                        </button>
                    </MenuItem>
                </MenuItems>
            </Menu>
        </div>
    );
}
